import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Info, LoaderCircle, X } from 'lucide-react';

import { API_MUTATION_ERROR_EVENT, API_MUTATION_START_EVENT, API_MUTATION_SUCCESS_EVENT,
  API_RETRY_EVENT, API_RETRY_SETTLED_EVENT } from '../../shared/http/transport.js';

const TOAST_LIFETIME_MS = 3600;
export const USER_NOTIFICATION_EVENT = 'eaxfuel:user-notification';

export function notifyUser(message, kind = 'success') {
  if (typeof globalThis.dispatchEvent !== 'function' || typeof globalThis.CustomEvent !== 'function') return;
  globalThis.dispatchEvent(new globalThis.CustomEvent(USER_NOTIFICATION_EVENT, { detail: { message, kind } }));
}

function integrationName(path) {
  if (path.includes('fatsecret')) return 'FatSecret';
  if (path.includes('google_calendar')) return 'Google Calendar';
  if (path.includes('open_wearables')) return 'Open Wearables';
  if (path.includes('twilio')) return 'Twilio SMS';
  return 'Integration';
}

export function mutationMessage({ method, path, operation, result }) {
  if (path === '/api/auth/sessions/revoke-others') return 'Other devices signed out.';
  if (method === 'DELETE' && path.startsWith('/api/auth/sessions/')) return 'Device signed out.';
  if (path.startsWith('/api/auth/') || path.startsWith('/api/client-errors')) return null;
  if (path === '/api/places' && method === 'POST' && result?.category === 'restaurant') return 'Recommendations updated.';
  if (path === '/api/profile') return 'Profile and goals updated.';
  if (path === '/api/hydration/goal') return 'Hydration goal updated.';
  if (path.includes('/hydration/') && path.includes('/entries')) return method === 'DELETE' ? 'Water entry removed.' : 'Water added.';
  if (path.startsWith('/api/food/') && path.includes('/entries')) return method === 'POST' ? 'Food added.' : method === 'DELETE' ? 'Food removed.' : 'Food updated.';
  if (path.startsWith('/api/food/library') && path.includes('/log/')) return 'Food added.';
  if (path.startsWith('/api/food/library')) return method === 'POST' ? 'Food saved to your library.' : method === 'DELETE' ? 'Saved food removed.' : 'Saved food updated.';
  if (path.startsWith('/api/fasting')) return 'Eating window updated.';
  if (path.startsWith('/api/groceries')) return method === 'POST' ? 'Grocery item added.' : method === 'DELETE' ? 'Grocery list updated.' : 'Grocery list updated.';
  if (path.startsWith('/api/calendar/events')) return method === 'POST' ? 'Calendar entry added.' : method === 'DELETE' ? 'Calendar entry removed.' : 'Calendar entry updated.';
  if (path.startsWith('/api/vitamins')) return method === 'POST' ? 'Vitamin added.' : method === 'DELETE' ? 'Vitamin removed.' : 'Vitamin log updated.';
  if (path.startsWith('/api/bloodwork')) return method === 'POST' ? 'Blood work added.' : method === 'DELETE' ? 'Blood work removed.' : 'Blood work updated.';
  if (path.startsWith('/api/weights')) return 'Weight updated.';
  if (path.startsWith('/api/training/settings')) return 'Training schedule updated.';
  if (path.includes('/training/swaps')) return method === 'DELETE' ? 'Workout swap undone.' : 'Workout days swapped successfully.';
  if (path.includes('/training/days/') && path.includes('/assignment')) return 'Training day updated.';
  if (path.includes('/training/days/') && path.includes('/logs/')) return 'Workout sets updated.';
  if (path.includes('/training/days/') && path.includes('/workout')) return 'Workout updated.';
  if (path.startsWith('/api/reminders/settings')) return 'Alert settings updated.';
  if (path.startsWith('/api/reminders/occurrences')) return operation === 'snoozed' ? 'Reminder snoozed for 15 minutes.'
    : operation === 'rescheduled' ? 'Reminder rescheduled.' : operation === 'done' ? 'Reminder completed.' : 'Reminder updated.';
  if (path.startsWith('/api/media')) return method === 'DELETE' ? 'Photo removed.' : 'Profile photo updated.';
  if (path === '/api/backup/import') return operation === 'preview' ? 'Backup verified and ready to review.' : 'Backup restored.';
  if (path.startsWith('/api/integrations/')) {
    const name = integrationName(path);
    if (path.endsWith('/begin')) return `${name} connection started.`;
    if (path.endsWith('/fatsecret/log')) return 'Food added from FatSecret.';
    if (path.endsWith('/sync')) return `${name} sync completed.`;
    if (path.endsWith('/test')) return 'Test SMS sent.';
    return method === 'DELETE' ? `${name} disconnected.` : `${name} connected.`;
  }
  return method === 'POST' ? 'Added successfully.' : method === 'DELETE' ? 'Removed successfully.' : 'Changes saved.';
}

export function mutationProgressMessage({ method, path, operation }) {
  if (path.startsWith('/api/auth/') || path.startsWith('/api/client-errors')) return null;
  if (path === '/api/backup/import') return operation === 'preview' ? 'Checking your backup...' : 'Restoring your backup...';
  if (path === '/api/profile') return 'Saving profile and goals...';
  if (path.startsWith('/api/hydration')) return method === 'DELETE' ? 'Removing water entry...' : 'Updating hydration...';
  if (path.startsWith('/api/food/') || path.startsWith('/api/fasting')) return method === 'DELETE' ? 'Removing food entry...' : 'Saving food changes...';
  if (path.startsWith('/api/groceries')) return 'Updating grocery list...';
  if (path.startsWith('/api/calendar')) return method === 'DELETE' ? 'Removing calendar entry...' : 'Saving calendar entry...';
  if (path.startsWith('/api/vitamins')) return 'Updating vitamins...';
  if (path.startsWith('/api/bloodwork')) return 'Saving blood work...';
  if (path.startsWith('/api/weights')) return 'Saving weight...';
  if (path.startsWith('/api/training')) return 'Updating training schedule...';
  if (path.startsWith('/api/reminders/occurrences')) return operation === 'snoozed' ? 'Snoozing reminder...'
    : operation === 'rescheduled' ? 'Rescheduling reminder...' : operation === 'done' ? 'Completing reminder...' : 'Updating reminder...';
  if (path.startsWith('/api/reminders')) return 'Saving reminder changes...';
  if (path.startsWith('/api/media')) return method === 'DELETE' ? 'Removing photo...' : 'Uploading photo...';
  if (path.startsWith('/api/integrations/')) { const name = integrationName(path);
    if (path.endsWith('/sync')) return `Syncing ${name}...`; if (path.endsWith('/test')) return 'Sending test SMS...';
    if (method === 'DELETE') return `Disconnecting ${name}...`; return `Connecting ${name}...`; }
  return method === 'DELETE' ? 'Removing...' : 'Saving changes...';
}

/** How many requests are currently between a retryable failure and giving up. */
const RETRY_TOAST_ID = 'connection-retry';

export function ToastViewport() {
  const [toasts, setToasts] = useState([]);
  const sequence = useRef(0);
  const timers = useRef(new Map());
  const retrying = useRef(0);

  /**
   * Say that a struggling request is still being retried.
   *
   * The transport has always retried network failures, 429s and 5xxs, but silently, so a
   * flaky connection looked like the app hanging and then failing. This shows a single
   * persistent message for as long as any request is retrying, however many are, and
   * clears it once they have all settled. Counted rather than boolean, so two overlapping
   * requests cannot have the first to finish take the message away from the second.
   */
  useEffect(() => {
    const clearRetry = () => {
      globalThis.clearTimeout(timers.current.get(RETRY_TOAST_ID));
      timers.current.delete(RETRY_TOAST_ID);
      setToasts((current) => current.filter((item) => item.id !== RETRY_TOAST_ID));
    };
    const started = () => {
      retrying.current += 1;
      setToasts((current) => [...current.filter((item) => item.id !== RETRY_TOAST_ID),
        { id: RETRY_TOAST_ID, kind: 'progress', message: 'Connection problem. Still trying...' }].slice(-3));
    };
    const settled = () => {
      retrying.current = Math.max(0, retrying.current - 1);
      if (retrying.current === 0) clearRetry();
    };
    globalThis.addEventListener(API_RETRY_EVENT, started);
    globalThis.addEventListener(API_RETRY_SETTLED_EVENT, settled);
    return () => {
      globalThis.removeEventListener(API_RETRY_EVENT, started);
      globalThis.removeEventListener(API_RETRY_SETTLED_EVENT, settled);
    };
  }, []);

  useEffect(() => {
    const dismiss = (id) => {
      globalThis.clearTimeout(timers.current.get(id));
      timers.current.delete(id);
      setToasts((current) => current.filter((item) => item.id !== id));
    };
    const receive = (event) => {
      const direct = event.type === USER_NOTIFICATION_EVENT;
      const progress = event.type === API_MUTATION_START_EVENT;
      const failed = event.type === API_MUTATION_ERROR_EVENT;
      const detail = event.detail ?? {};
      if (!direct && failed && !mutationProgressMessage(detail)) return;
      const message = direct ? detail.message : progress ? mutationProgressMessage(detail) : failed ? detail.message : mutationMessage(detail);
      if (!message) return;
      const id = direct ? ++sequence.current : `request-${detail.requestId}`;
      const kind = direct ? detail.kind ?? 'success' : progress ? 'progress' : failed ? 'error' : 'success';
      setToasts((current) => [...current.filter((item) => item.id !== id && item.message !== message), { id, message, kind }].slice(-3));
      globalThis.clearTimeout(timers.current.get(id));
      if (!progress) timers.current.set(id, globalThis.setTimeout(() => dismiss(id), TOAST_LIFETIME_MS));
    };
    globalThis.addEventListener(API_MUTATION_START_EVENT, receive);
    globalThis.addEventListener(API_MUTATION_SUCCESS_EVENT, receive);
    globalThis.addEventListener(API_MUTATION_ERROR_EVENT, receive);
    globalThis.addEventListener(USER_NOTIFICATION_EVENT, receive);
    return () => {
      globalThis.removeEventListener(API_MUTATION_START_EVENT, receive);
      globalThis.removeEventListener(API_MUTATION_SUCCESS_EVENT, receive);
      globalThis.removeEventListener(API_MUTATION_ERROR_EVENT, receive);
      globalThis.removeEventListener(USER_NOTIFICATION_EVENT, receive);
      for (const timer of timers.current.values()) globalThis.clearTimeout(timer);
      timers.current.clear();
    };
  }, []);

  const dismiss = (id) => {
    globalThis.clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((current) => current.filter((item) => item.id !== id));
  };

  return <div className="toast-viewport" aria-live="polite" aria-label="Update notifications">
    {toasts.map((toast) => <div className={`app-toast app-toast--${toast.kind} animate-toast-in`} role="status" key={toast.id}>
      {toast.kind === 'success' ? <CheckCircle2 size={18} aria-hidden="true" /> : toast.kind === 'progress' ? <LoaderCircle className="app-toast__spinner" size={18} aria-hidden="true" /> : toast.kind === 'error' ? <AlertCircle size={18} aria-hidden="true" /> : <Info size={18} aria-hidden="true" />}
      <span>{toast.message}</span>
      <button type="button" aria-label="Dismiss notification" onClick={() => dismiss(toast.id)}><X size={15} aria-hidden="true" /></button>
    </div>)}
  </div>;
}
